import React from 'react';
import { AiOutlinePlus, AiOutlineClose, AiOutlineArrowUp, AiOutlineArrowDown } from 'react-icons/ai';

/**
 * Editor visual de transforms de post-processing para un campo OCR_AI (FSD-007).
 *
 * Los transforms se aplican en el orden en que aparecen. Cada transform tiene un `type`
 * + args específicos. El backend valida en el save que los tipos sean válidos y los args
 * obligatorios estén presentes.
 *
 * Props:
 *  - transforms: array actual (puede ser undefined)
 *  - onChange(newTransforms: array)
 *  - seriesHasDocuments: boolean — si true, muestra hint informativo de impacto retroactivo
 */

// Catálogo de tipos soportados. Debe mantenerse en sync con MetadataTransformType (enum Java).
const TRANSFORM_TYPES = [
  {
    value: 'strip_prefix',
    label: 'Sacar prefijo',
    args: [{ name: 'value', label: 'Prefijo a quitar', type: 'text', required: true }],
  },
  {
    value: 'strip_suffix',
    label: 'Sacar sufijo',
    args: [{ name: 'value', label: 'Sufijo a quitar', type: 'text', required: true }],
  },
  {
    value: 'strip_leading_zeros',
    label: 'Sacar ceros iniciales',
    args: [],
  },
  {
    value: 'cut_at_substring',
    label: 'Cortar en substring',
    args: [
      { name: 'separator', label: 'Separador', type: 'text', required: true },
      {
        name: 'keep',
        label: 'Conservar',
        type: 'select',
        required: true,
        options: [
          { value: 'before', label: 'Lo de antes' },
          { value: 'after', label: 'Lo de después' },
        ],
      },
    ],
  },
  {
    value: 'regex_replace',
    label: 'Reemplazar con regex',
    args: [
      { name: 'pattern', label: 'Regex', type: 'text', required: true },
      { name: 'replacement', label: 'Reemplazo', type: 'text', required: true },
    ],
  },
  { value: 'upper', label: 'Mayúsculas', args: [] },
  { value: 'lower', label: 'Minúsculas', args: [] },
  { value: 'trim', label: 'Trim (quitar espacios)', args: [] },
  {
    value: 'reject_if_matches',
    label: 'Rechazar si matchea regex',
    args: [{ name: 'pattern', label: 'Regex', type: 'text', required: true }],
  },
  {
    value: 'insert_spaces_on_camel',
    label: 'Insertar espacios en CamelCase',
    args: [],
  },
];

const getTypeSpec = (type) => TRANSFORM_TYPES.find((t) => t.value === type);

const TransformsEditor = ({ transforms = [], onChange, seriesHasDocuments }) => {
  const list = Array.isArray(transforms) ? transforms : [];

  const update = (index, next) => {
    const copy = [...list];
    copy[index] = next;
    onChange(copy);
  };

  const add = () => {
    onChange([...list, { type: '' }]);
  };

  const remove = (index) => {
    onChange(list.filter((_, i) => i !== index));
  };

  const move = (index, delta) => {
    const copy = [...list];
    const newIdx = index + delta;
    if (newIdx < 0 || newIdx >= copy.length) return;
    [copy[index], copy[newIdx]] = [copy[newIdx], copy[index]];
    onChange(copy);
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <label style={styles.title}>Transforms de post-processing</label>
        <button type="button" onClick={add} style={styles.addBtn}>
          <AiOutlinePlus size={12} /> Agregar transform
        </button>
      </div>

      <p style={styles.hint}>
        Se aplican en orden al valor que devuelve la IA, antes de guardarlo. Útil para limpiar
        prefijos, normalizar formatos o descartar categorías genéricas.
      </p>

      {seriesHasDocuments && (
        <p style={styles.warningBox}>
          Los transforms se aplicarán solo a documentos procesados a partir de ahora. Los
          documentos ya procesados no se re-procesan automáticamente.
        </p>
      )}

      {list.length === 0 ? (
        <p style={styles.empty}>Sin transforms. Agregá uno si el valor del LLM necesita limpieza.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {list.map((t, i) => (
            <TransformRow
              key={i}
              transform={t}
              index={i}
              total={list.length}
              onChange={(next) => update(i, next)}
              onDelete={() => remove(i)}
              onMoveUp={() => move(i, -1)}
              onMoveDown={() => move(i, +1)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const TransformRow = ({ transform, index, total, onChange, onDelete, onMoveUp, onMoveDown }) => {
  const spec = getTypeSpec(transform.type);

  const setType = (newType) => {
    // al cambiar el tipo, preservar solo los args que todavía apliquen
    const newSpec = getTypeSpec(newType);
    const next = { type: newType };
    if (newSpec) {
      for (const arg of newSpec.args) {
        if (transform[arg.name] !== undefined) next[arg.name] = transform[arg.name];
      }
    }
    onChange(next);
  };

  const setArg = (argName, value) => {
    onChange({ ...transform, [argName]: value });
  };

  return (
    <div style={styles.row}>
      <span style={styles.orderIdx}>{index + 1}</span>

      <select value={transform.type || ''} onChange={(e) => setType(e.target.value)} style={styles.typeSelect}>
        <option value="">-- Elegir tipo --</option>
        {TRANSFORM_TYPES.map((t) => (
          <option key={t.value} value={t.value}>
            {t.label}
          </option>
        ))}
      </select>

      <div style={styles.argsContainer}>
        {spec?.args.map((arg) => (
          <ArgInput
            key={arg.name}
            arg={arg}
            value={transform[arg.name] ?? ''}
            onChange={(v) => setArg(arg.name, v)}
          />
        ))}
      </div>

      <div style={styles.controls}>
        <button
          type="button"
          disabled={index === 0}
          onClick={onMoveUp}
          style={{ ...styles.iconBtn, opacity: index === 0 ? 0.3 : 1 }}
          title="Subir"
          aria-label="Subir transform"
        >
          <AiOutlineArrowUp size={12} />
        </button>
        <button
          type="button"
          disabled={index === total - 1}
          onClick={onMoveDown}
          style={{ ...styles.iconBtn, opacity: index === total - 1 ? 0.3 : 1 }}
          title="Bajar"
          aria-label="Bajar transform"
        >
          <AiOutlineArrowDown size={12} />
        </button>
        <button
          type="button"
          onClick={onDelete}
          style={{ ...styles.iconBtn, color: '#DC2626' }}
          title="Eliminar"
          aria-label="Eliminar transform"
        >
          <AiOutlineClose size={12} />
        </button>
      </div>
    </div>
  );
};

const ArgInput = ({ arg, value, onChange }) => {
  if (arg.type === 'select') {
    return (
      <select value={value} onChange={(e) => onChange(e.target.value)} style={styles.argInput}>
        <option value="">{arg.label}</option>
        {arg.options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    );
  }
  return (
    <input
      type="text"
      value={value}
      placeholder={arg.label}
      onChange={(e) => onChange(e.target.value)}
      style={styles.argInput}
    />
  );
};

// ── Estilos ────────────────────────────────────────────────────────────────

const styles = {
  container: {
    marginTop: 10,
    paddingTop: 10,
    borderTop: '1px dashed #E5E7EB',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  title: {
    display: 'block',
    fontSize: 13,
    fontWeight: 500,
    color: '#374151',
  },
  addBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '3px 8px',
    backgroundColor: 'transparent',
    color: '#00468B',
    border: '1px solid #C7DBF0',
    borderRadius: 4,
    fontSize: 11,
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
  },
  hint: {
    fontSize: 11,
    color: '#6B7280',
    margin: '2px 0 8px 0',
  },
  warningBox: {
    fontSize: 11,
    color: '#92400E',
    backgroundColor: '#FFFBEB',
    border: '1px solid #FCD34D',
    padding: '6px 10px',
    borderRadius: 4,
    marginBottom: 8,
  },
  empty: {
    fontSize: 11,
    color: '#9CA3AF',
    fontStyle: 'italic',
    margin: '4px 0',
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '20px 150px 1fr auto',
    alignItems: 'center',
    gap: 6,
    padding: '4px 0',
  },
  orderIdx: {
    fontSize: 11,
    color: '#9CA3AF',
    fontWeight: 600,
    textAlign: 'center',
    fontFamily: 'Menlo, monospace',
  },
  typeSelect: {
    padding: '4px 6px',
    border: '1px solid #D1D5DB',
    borderRadius: 4,
    fontSize: 12,
    backgroundColor: '#fff',
  },
  argsContainer: {
    display: 'flex',
    gap: 6,
    flexWrap: 'wrap',
  },
  argInput: {
    padding: '4px 6px',
    border: '1px solid #D1D5DB',
    borderRadius: 4,
    fontSize: 12,
    flex: 1,
    minWidth: 80,
  },
  controls: {
    display: 'flex',
    gap: 2,
  },
  iconBtn: {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: '#6B7280',
    padding: 3,
    lineHeight: 0,
  },
};

export default TransformsEditor;
